// src/modules/accounting/pages/TrialBalancePage.jsx

import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Spin, Alert, Typography, Row, Col, DatePicker, Button, Space, Tag,
  Statistic, Segmented, Empty, Tooltip
} from 'antd';
import { ReloadOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getGroupedTrialBalance, verifyTrialBalance } from '../api';
import DataTable from '../../../components/UI/DataTable';

const { Title, Text } = Typography;

export default function TrialBalancePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [asOfDate, setAsOfDate] = useState(null);
  const [verification, setVerification] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (asOfDate) params.asOfDate = asOfDate.endOf('day').toISOString();
      params.includeZero = false;
      const [tbRes, verifyRes] = await Promise.all([
        getGroupedTrialBalance(params),
        verifyTrialBalance(params),
      ]);
      setData(tbRes.data || tbRes);
      setVerification(verifyRes.data || verifyRes);
    } catch (err) {
      setError(err.message || 'Failed to load trial balance');
    } finally {
      setLoading(false);
    }
  }, [asOfDate]);

  useEffect(() => { fetch(); }, [fetch]);

  const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');
  const typeColors = { ASSET: 'blue', LIABILITY: 'red', EQUITY: 'purple', REVENUE: 'green', EXPENSE: 'orange' };

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '80px auto' }} />;
  if (error) return <Alert type="error" message={error} showIcon />;
  if (!data) return <Empty />;

  const groups = data.groups || [];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={5} style={{ margin: 0 }}>Trial Balance</Title></Col>
        <Col>
          <Space>
            <DatePicker value={asOfDate} onChange={setAsOfDate} format="DD/MM/YYYY" placeholder="As of date" allowClear />
            <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={fetch} /></Tooltip>
          </Space>
        </Col>
      </Row>

      {/* Verification Status */}
      {verification && (
        <Card size="small" style={{ marginBottom: 16, background: verification.isBalanced ? '#f6ffed' : '#fff2e8' }}>
          <Row gutter={16} align="middle">
            <Col>
              {verification.isBalanced
                ? <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                : <WarningOutlined style={{ fontSize: 24, color: '#faad14' }} />}
            </Col>
            <Col flex="auto">
              <Text strong>{verification.isBalanced ? 'Trial Balance is Balanced' : 'Trial Balance is IMBALANCED'}</Text>
              {!verification.isBalanced && <Text type="danger"> — Difference: ₹{fmt(verification.difference)}</Text>}
            </Col>
            <Col><Statistic title="Total Debit" value={fmt(verification.totalDebit)} prefix="₹" /></Col>
            <Col><Statistic title="Total Credit" value={fmt(verification.totalCredit)} prefix="₹" /></Col>
          </Row>
        </Card>
      )}

      {/* Grouped Trial Balance (by account nature, statement order) */}
      {groups.map((group) => (
        <Card
          key={group.nature || group.type}
          title={
            <Space>
              <Tag color={typeColors[group.coarseType] || 'default'}>{group.label || group.type}</Tag>
              {group.statement && <Text type="secondary" style={{ fontSize: 12 }}>{group.statement === 'BALANCE_SHEET' ? 'Balance Sheet' : 'Profit & Loss'}</Text>}
            </Space>
          }
          size="small"
          style={{ marginBottom: 12 }}
          extra={<Text strong>Subtotal: Debit ₹{fmt(group.subtotal?.debit)} | Credit ₹{fmt(group.subtotal?.credit)}</Text>}
        >
          <DataTable persistId="accounting.trial-balance.group"
            dataSource={(group.accounts || []).map((a, i) => ({
              ...a.account,
              debit: a.debitBalance,
              credit: a.creditBalance,
              isGroup: a.isGroup,
              descendantCount: a.descendantCount,
              rollupNetBalance: a.rollupNetBalance,
              isChild: !!a.account?.parentAccount,
              key: i,
            }))}
            columns={[
              { title: 'Code', dataIndex: 'code', width: 80 },
              {
                title: 'Account',
                dataIndex: 'name',
                render: (name, rec) => (
                  <Space size={4}>
                    {rec.isChild && <Text type="secondary" style={{ fontFamily: 'monospace' }}>↳</Text>}
                    <span>{name}</span>
                    {rec.isGroup && (
                      <Tooltip title={`Group heading. Its ${rec.descendantCount} child account(s) are listed separately and are what the totals count — the subtotal here is shown for reference only, never added twice.`}>
                        <Tag color="geekblue">Group</Tag>
                      </Tooltip>
                    )}
                  </Space>
                ),
              },
              {
                title: 'Debit', dataIndex: 'debit', align: 'right', width: 140,
                render: (v, rec) => {
                  if (v > 0) return `₹${fmt(v)}`;
                  // A group with no postings of its own shows its branch subtotal
                  // instead of a bare blank — that blank is what made 2400 look empty.
                  if (rec.isGroup && rec.rollupNetBalance > 0 && rec.normalBalance === 'DEBIT') {
                    return <Text type="secondary" italic>₹{fmt(rec.rollupNetBalance)}</Text>;
                  }
                  return '';
                },
              },
              {
                title: 'Credit', dataIndex: 'credit', align: 'right', width: 140,
                render: (v, rec) => {
                  if (v > 0) return `₹${fmt(v)}`;
                  if (rec.isGroup && rec.rollupNetBalance > 0 && rec.normalBalance === 'CREDIT') {
                    return <Text type="secondary" italic>₹{fmt(rec.rollupNetBalance)}</Text>;
                  }
                  return '';
                },
              },
            ]}
            pagination={false}
            size="small"
          />
        </Card>
      ))}

      {/* Grand Totals */}
      {data.totals && (
        <Card size="small" style={{ background: '#fafafa' }}>
          <Row justify="end" gutter={32}>
            <Col><Statistic title="Grand Total Debit" value={fmt(data.totals.debit)} prefix="₹" valueStyle={{ fontWeight: 'bold' }} /></Col>
            <Col><Statistic title="Grand Total Credit" value={fmt(data.totals.credit)} prefix="₹" valueStyle={{ fontWeight: 'bold' }} /></Col>
          </Row>
        </Card>
      )}
    </div>
  );
}
